import { CreateFinancialsDto, FinancialsEntity, UpdateFinancialsDto } from '@crm/dto';
import { ApiResponseInterface, ApiResponseStatus, FinancialsStatus } from '@crm/interface';
import { API_RESPONSE, prepareUpdateFields } from '@crm/utils';
import { Injectable, Logger } from '@nestjs/common';
import { InjectRepository } from '@nestjs/typeorm';
import { Repository } from 'typeorm';

@Injectable()
export class FinancialsService {
  private readonly logger = new Logger(FinancialsService.name);

  constructor(
    @InjectRepository(FinancialsEntity)
    private readonly financialsRepo: Repository<FinancialsEntity>,
  ) {}

  // Get all financials
  async getAll(): Promise<ApiResponseInterface> {
    try {
      const financials = await this.financialsRepo.find({
        relations: ['created_by', 'updated_by'],
      });

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.FINANCIALS.GET_ALL.CODE,
        message: API_RESPONSE.SUCCESS.FINANCIALS.GET_ALL.MESSAGE,
        data: financials,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.FINANCIALS.GET_ALL.CODE,
        message: API_RESPONSE.ERROR.FINANCIALS.GET_ALL.MESSAGE,
        data: error,
      };
    }
  }

  // Get financials by user ID (creator)
  async getByUserId(userId: string): Promise<ApiResponseInterface> {
    try {
      const financials = await this.financialsRepo.find({
        where: { created_by: { id: userId } },
        relations: ['created_by', 'updated_by'],
      });

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.FINANCIALS.GET_BY_ID.CODE,
        message: API_RESPONSE.SUCCESS.FINANCIALS.GET_BY_ID.MESSAGE,
        data: financials,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.FINANCIALS.GET_BY_ID.CODE,
        message: API_RESPONSE.ERROR.FINANCIALS.GET_BY_ID.MESSAGE,
        data: error,
      };
    }
  }

  // Get financials by ID
  async getById(id: string): Promise<ApiResponseInterface> {
    try {
      const financial = await this.financialsRepo.findOne({
        where: { id },
        relations: ['created_by', 'updated_by'],
      });

      if (!financial) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.FINANCIALS.GET_BY_ID.CODE,
          message: API_RESPONSE.ERROR.FINANCIALS.GET_BY_ID.MESSAGE,
          data: null,
        };
      }

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.FINANCIALS.GET_BY_ID.CODE,
        message: API_RESPONSE.SUCCESS.FINANCIALS.GET_BY_ID.MESSAGE,
        data: financial,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.FINANCIALS.GET_BY_ID.CODE,
        message: API_RESPONSE.ERROR.FINANCIALS.GET_BY_ID.MESSAGE,
        data: error,
      };
    }
  }

  // Create a new financial record
  async create(dto: CreateFinancialsDto): Promise<ApiResponseInterface> {
    try {
      const newFinancial = this.financialsRepo.create(dto);
      await this.financialsRepo.save(newFinancial);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.FINANCIALS.CREATE.CODE,
        message: API_RESPONSE.SUCCESS.FINANCIALS.CREATE.MESSAGE,
        data: newFinancial,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.FINANCIALS.CREATE.CODE,
        message: API_RESPONSE.ERROR.FINANCIALS.CREATE.MESSAGE,
        data: error,
      };
    }
  }

  // Update existing financial record
  async update(dto: UpdateFinancialsDto): Promise<ApiResponseInterface> {
    try {
      const financial = await this.financialsRepo.findOne({
        where: { id: dto.id },
        relations: ['updated_by'],
      });

      if (!financial) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.FINANCIALS.UPDATE.CODE,
          message: API_RESPONSE.ERROR.FINANCIALS.UPDATE.MESSAGE,
          data: null,
        };
      }

      const updateData = prepareUpdateFields(dto, {
        updated_by_id: 'updated_by',
      });

      Object.assign(financial, updateData);
      await this.financialsRepo.save(financial);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.FINANCIALS.UPDATE.CODE,
        message: API_RESPONSE.SUCCESS.FINANCIALS.UPDATE.MESSAGE,
        data: financial,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.FINANCIALS.UPDATE.CODE,
        message: API_RESPONSE.ERROR.FINANCIALS.UPDATE.MESSAGE,
        data: error,
      };
    }
  }

  // Soft delete financial record
  async softDelete(dto: UpdateFinancialsDto): Promise<ApiResponseInterface> {
    try {
      const financial = await this.financialsRepo.findOne({
        where: { id: dto.id },
      });

      if (!financial) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.FINANCIALS.DELETE.CODE,
          message: API_RESPONSE.ERROR.FINANCIALS.DELETE.MESSAGE,
          data: null,
        };
      }

      financial.status = FinancialsStatus.DELETED;
      await this.financialsRepo.save(financial);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.FINANCIALS.DELETE.CODE,
        message: API_RESPONSE.SUCCESS.FINANCIALS.DELETE.MESSAGE,
        data: financial,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.FINANCIALS.DELETE.CODE,
        message: API_RESPONSE.ERROR.FINANCIALS.DELETE.MESSAGE,
        data: error,
      };
    }
  }
}